import React, { useMemo } from 'react';
import { DRIVER_ORDER } from '@/data/clubDemoData';
import { SectionTitle, InsightBox, TrendBadge, MR, JB } from './DemoShared';

export function Benchmark({ club, clubs, dt, lang }) {
  const b = dt.benchmark;
  const sorted = useMemo(() => [...clubs].sort((a, b) => b.brandScore - a.brandScore), [clubs]);
  const maxVal = useMemo(() => Math.max(...clubs.map((c) => c.brandValue)), [clubs]);
  const rank = sorted.findIndex((c) => c.id === club.id) + 1;

  return (
    <section data-testid="section-benchmark">
      <SectionTitle
        label={b.eyebrow}
        title={b.title}
        subtitle={b.subtitle}
      />

      <div className="rounded-xl border border-border/40 bg-card/50 p-5 mb-6 flex items-center gap-5" data-testid="rank-callout">
        <div className="text-center">
          <span className="text-3xl font-bold text-primary" style={MR}>#{rank}</span>
          <p className="text-[11px] text-muted-foreground/50 mt-0.5" style={JB}>of {clubs.length}</p>
        </div>
        <div className="h-10 w-px bg-border/40" />
        <p className="text-sm text-foreground/80 leading-relaxed">
          {rank === 1 ? b.rank1 : rank <= 2 ? b.rank2 : b.rankN(rank, clubs.length)}
        </p>
      </div>

      <div className="rounded-xl border border-border/40 bg-card/30 overflow-hidden mb-6" data-testid="benchmark-table">
        <div className="grid grid-cols-[1fr_80px_120px_60px] sm:grid-cols-[1fr_80px_140px_80px] gap-3 px-5 py-3 border-b border-border/30 text-[11px] text-muted-foreground/50 uppercase tracking-wider" style={JB}>
          <span>{b.club}</span><span className="text-right">{b.score}</span><span>{b.brandValueLabel}</span><span className="text-right">{b.trend}</span>
        </div>
        {sorted.map((c) => {
          const isActive = c.id === club.id;
          return (
            <div key={c.id} className={`grid grid-cols-[1fr_80px_120px_60px] sm:grid-cols-[1fr_80px_140px_80px] gap-3 px-5 py-3.5 items-center border-b border-border/20 last:border-0 transition-colors ${isActive ? 'bg-primary/[0.06]' : ''}`} data-testid={`benchmark-row-${c.id}`}>
              <div className="flex items-center gap-2.5 min-w-0">
                <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: c.color }} />
                <span className={`text-sm truncate ${isActive ? 'font-semibold text-foreground' : 'text-foreground/70'}`}>{c.name}</span>
              </div>
              <span className={`text-right text-sm font-medium ${isActive ? 'text-primary' : 'text-foreground/70'}`} style={MR}>{c.brandScore}</span>
              <div className="flex items-center gap-2">
                <div className="flex-1 h-1.5 rounded-full bg-muted/30 overflow-hidden">
                  <div className="h-full rounded-full bg-primary/60 transition-all duration-500" style={{ width: `${(c.brandValue / maxVal) * 100}%` }} />
                </div>
                <span className="text-xs text-muted-foreground" style={JB}>&euro;{c.brandValue}M</span>
              </div>
              <span className="text-right"><TrendBadge value={c.trend} /></span>
            </div>
          );
        })}
      </div>

      <DriverComparison club={club} clubs={clubs} dt={dt} />

      <InsightBox variant="warning">
        {club.benchmarkInsight}
      </InsightBox>
    </section>
  );
}

function DriverComparison({ club, clubs, dt }) {
  return (
    <div className="rounded-xl border border-border/40 bg-card/30 p-5 mb-6" data-testid="benchmark-drivers">
      <p className="text-xs text-muted-foreground/50 uppercase tracking-wider mb-4" style={JB}>{dt.benchmark.driverComparison}</p>
      {DRIVER_ORDER.map((key) => {
        const avg = Math.round(clubs.reduce((s, c) => s + c.drivers[key], 0) / clubs.length);
        const val = club.drivers[key];
        const diff = val - avg;
        return (
          <div key={key} className="flex items-center gap-3 py-2">
            <span className="text-xs text-muted-foreground w-[140px] sm:w-[180px] truncate">{dt.driverLabels[key]}</span>
            <div className="flex-1 flex items-center gap-2">
              <div className="flex-1 h-1.5 rounded-full bg-muted/30 relative overflow-hidden">
                <div className="absolute h-full rounded-full bg-primary/40" style={{ width: `${avg}%` }} />
                <div className={`absolute h-full rounded-full ${val >= avg ? 'bg-[hsl(150,50%,45%)]' : 'bg-[hsl(38,85%,55%)]'}`} style={{ width: `${val}%` }} />
              </div>
              <span className={`text-[11px] font-medium w-10 text-right ${diff >= 0 ? 'text-[hsl(150,50%,45%)]' : 'text-[hsl(38,85%,55%)]'}`} style={JB}>
                {diff >= 0 ? '+' : ''}{diff}
              </span>
            </div>
          </div>
        );
      })}
    </div>
  );
}
